import { useSearchParams } from "react-router-dom";
import { PrototypeSwitcher, type PrototypeVariant } from "../../../components/PrototypeSwitcher";
import type { JobCardFaceProps } from "./cardFaceTypes";
import { VariantAAddressLed, VARIANT_A_NAME } from "./CardFaceAAddressLed";
import { VariantBLedger, VARIANT_B_NAME } from "./CardFaceBLedger";
import { VariantCCompact, VARIANT_C_NAME } from "./CardFaceCCompact";
import { VariantDTriage, VARIANT_D_NAME } from "./CardFaceDTriage";

/**
 * PROTOTYPE ONLY — throwaway. Delete `src/features/jobs/prototype/CardFace*`
 * and this file once a layout is picked.
 *
 * Question: what belongs on a job card's face, and how should it be arranged?
 * Four variants of the same card, on the existing /jobs board, switchable via
 * `?cardVariant=A|B|C|D` or the floating bottom bar. Same `JobCard` data, same
 * board, same drag behaviour — only the face changes.
 *
 * They disagree on purpose:
 *   A — the address is the job's identity; scope text is the subtitle.
 *   B — a document: body block, hairline, meta bar; money right-anchored.
 *   C — three lines, nothing else; twice as many cards on screen.
 *   D — status first; the card answers "does this need me today?".
 */
export const JOB_CARD_VARIANTS: readonly PrototypeVariant[] = [
  { key: "0", name: "Current face" },
  { key: "A", name: VARIANT_A_NAME },
  { key: "B", name: VARIANT_B_NAME },
  { key: "C", name: VARIANT_C_NAME },
  { key: "D", name: VARIANT_D_NAME },
];

export const JOB_CARD_VARIANT_PARAM = "cardVariant";

/** The variant currently selected in the URL, defaulting to the live face. */
export function useJobCardVariant(): string {
  const [searchParams] = useSearchParams();
  const requested = (searchParams.get(JOB_CARD_VARIANT_PARAM) ?? "0").toUpperCase();
  return JOB_CARD_VARIANTS.some((entry) => entry.key === requested) ? requested : "0";
}

/** Renders the prototype face for a variant, or null for "0" (the live face). */
export function renderJobCardVariant(variant: string, props: JobCardFaceProps) {
  if (variant === "A") return <VariantAAddressLed {...props} />;
  if (variant === "B") return <VariantBLedger {...props} />;
  if (variant === "C") return <VariantCCompact {...props} />;
  if (variant === "D") return <VariantDTriage {...props} />;
  return null;
}

/** The floating bar; hidden outside dev builds by the switcher itself. */
export function JobCardFaceSwitcher({ current }: { current: string }) {
  return (
    <PrototypeSwitcher
      variants={JOB_CARD_VARIANTS}
      current={current}
      param={JOB_CARD_VARIANT_PARAM}
    />
  );
}
